<template>
  <div class="mt-4">
    <div class="form-row">
      <div class="col-12 mb-3">
        <h5 class="text-primary">{{ tr('Deployments') }}</h5>
      </div>
    </div>

    <div class="form-row form-material p-4">
      <select-input :fieldOptions="customers" :currentValue.sync="localSelected" class="col-4"/>

      <div class="col-8 d-flex align-items-end">
        <button
          type="button"
          class="btn btn-success mb-2 mx-1"
          @click="onStart"
          :disabled="!localSelected || isDeploying || hasActiveDeployment(localSelected)"
        >
          <i class="fa fa-rocket"></i> {{ tr('Deploy') }} {{ localSelected }}
        </button>

        <button
          v-if="hasActiveDeployment(localSelected)"
          type="button"
          class="btn btn-danger mb-2 mx-1"
          @click="onStop"
        >
          <i class="fa fa-stop"></i> {{ tr('Stop') }}
        </button>

        <span v-if="isDeploying" class="text-info mb-2 mx-1">
          <i class="fa fa-spinner fa-spin"></i> {{ tr('Starting...') }}
        </span>
      </div>
    </div>

    <div v-if="Object.keys(activeDeployments).length > 0" class="row mt-3">
      <div class="col-12">
        <h6 class="text-secondary">{{ tr('Active Deployments') }}</h6>
        <div v-for="(deployment, env) in activeDeployments" :key="env" class="mb-3">
          <div class="card">
            <div class="card-body py-2">
              <div class="row align-items-center">
                <div class="col-md-6">
                  <h6 class="text-primary font-weight-bold mb-1">{{ env.toUpperCase() }}</h6>
                  <span :class="'badge ' + getStatusBadgeClass(deployment.status)">
                    {{ deployment.status || 'Unknown' }}
                  </span>
                </div>
                <div class="col-md-6 text-right">
                  <small class="text-muted d-block">{{ tr('User') }}: {{ deployment.user_name || 'Unknown' }}</small>
                  <small class="text-muted">{{ tr('Duration') }}: {{ formatDuration(deployment) }}</small>
                </div>
              </div>
              <div class="progress mt-2" style="height: 4px;">
                <div class="progress-bar" :class="getProgressBarClass(deployment.status)" :style="{ width: getProgressWidth(deployment.status) }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'deployments-panel',
  props: {
    customers: { type: Array, default: () => [] },
    activeDeployments: { type: Object, default: () => ({}) },
    isDeploying: { type: Boolean, default: false },
    selectedEnvironment: { type: String, default: '' }
  },
  data () {
    return { localSelected: this.selectedEnvironment }
  },
  watch: {
    selectedEnvironment (v) { this.localSelected = v },
    localSelected (v) { this.$emit('update:selectedEnvironment', v) }
  },
  methods: {
    onStart () { this.$emit('start', this.localSelected) },
    onStop () { this.$emit('stop', this.localSelected) },
    hasActiveDeployment (environment) {
      if (!environment || !this.activeDeployments) return false
      const deployment = this.activeDeployments[environment]
      if (!deployment || !deployment.status) return false
      return ['IN_PROGRESS', 'PENDING', 'SUBMITTED'].includes(deployment.status)
    },
    getStatusBadgeClass (status) {
      if (!status) return 'badge-secondary'
      const classes = {
        'IN_PROGRESS': 'badge-primary',
        'PENDING': 'badge-warning',
        'SUBMITTED': 'badge-info',
        'SUCCEEDED': 'badge-success',
        'FAILED': 'badge-danger',
        'STOPPED': 'badge-secondary',
        'TIMED_OUT': 'badge-danger'
      }
      return classes[status] || 'badge-secondary'
    },
    getProgressBarClass (status) {
      if (!status) return 'bg-secondary'
      const classes = {
        'IN_PROGRESS': 'bg-primary',
        'PENDING': 'bg-warning',
        'SUBMITTED': 'bg-info',
        'SUCCEEDED': 'bg-success',
        'FAILED': 'bg-danger',
        'STOPPED': 'bg-secondary',
        'TIMED_OUT': 'bg-danger'
      }
      return classes[status] || 'bg-secondary'
    },
    getProgressWidth (status) {
      if (!status) return '0%'
      const widths = {
        'PENDING': '25%',
        'SUBMITTED': '50%',
        'IN_PROGRESS': '75%',
        'SUCCEEDED': '100%',
        'FAILED': '100%',
        'STOPPED': '100%',
        'TIMED_OUT': '100%'
      }
      return widths[status] || '0%'
    },
    formatDuration (deployment) {
      if (!deployment || !deployment.start_time) return '0s'
      const start = new Date(deployment.start_time)
      const end = deployment.end_time ? new Date(deployment.end_time) : new Date()
      const diff = Math.floor((end - start) / 1000)
      if (diff < 60) return `${diff}s`
      if (diff < 3600) return `${Math.floor(diff / 60)}m ${diff % 60}s`
      return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`
    },
    tr (key) {
      if (window.tr && typeof window.tr === 'function') return window.tr(key)
      if (this.$t && typeof this.$t === 'function') return this.$t(key)
      return key
    }
  }
}
</script>

<style scoped>
.deployment-card { transition: all 0.3s ease; }
.deployment-card.active { box-shadow: 0 0 15px rgba(0, 123, 255, 0.3); }
</style>
